import axios from "axios";
import BigNumber from "bignumber.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Multicall } from "ethereum-multicall";
import Web3 from "web3";
import Web3Modal from "../../factories/web3/Web3Modal";
import OnChainDataInfo, {
  processMulticallCallsReturnContext,
  executeMulticall,
} from "../../factories/OnChainDataInfo";
import EnsUtils from "../../factories/EnsUtils";
import CCIPBridge from "../../factories/web3/CCIPBridge";
import ERC20, { abi as erc20Abi } from "../../factories/web3/ERC20";
import LendingPool from "../../factories/web3/LendingPool";
import LendingPoolExtLP from "../../factories/web3/LendingPoolExtLP";
import LendingRewards from "../../factories/web3/LendingRewards";
import {
  exponentialBackoff,
  formatDynamicDecimals,
  getShortAddress,
  processMulticallValue,
} from "../../factories/Utils";
dayjs.extend(utc);

export default {
  state: () => ({
    // ensRpcUrl: `https://ethereum.publicnode.com`,
    ensRpcUrl: "https://eth-pokt.nodies.app",
    denomenator: "10000",
    zeroAddress: "0x0000000000000000000000000000000000000000",
    dataRefreshInterval: null,
    initLoading: false,
    builtInWeb3Provider: window.ethereum,
    selectedNetworkShortName: "eth",
    web3: {
      instance: null,
      isConnected: false,
      chainId: null,
      address: "",
    },
    addressEnsMap: {},
    networks: [
      {
        name: "Arbitrum One",
        short_name: "arbitrum",
        chain: "arbitrum",
        network: "arbitrum",
        chain_id: 42161,
        network_id: 42161,
        chart_url:
          "https://www.dextools.io/app/en/arbitrum-one/pair-explorer/",
        explorer_url: "https://arbiscan.io/",
        rpc_url: "https://arb1.arbitrum.io/rpc",
        dex_url:
          "https://app.uniswap.org/#/swap?inputCurrency=ETH&outputCurrency=%TOKEN%",
        blocks_per_day: 999999999,
        native_currency: {
          symbol: "ETH",
          name: "ETH",
          decimals: 18,
          contractAddress:
            "0x82af49447d8a07e3bd95bd0d56f35241523fbab1",
          balance: "",
        },
        logo: `/networks/arbitrum.svg`,
        contracts: {
          hype: "0x85225Ed797fd4128Ac45A992C46eA4681a7A15dA",

          hlp: "0xAef79d508840087bBF2279bb4026d941Ec465984",
          lendingPool: "0xcD3E2aDCCF21198037E0eca780902E41F3C84838",
          loanToken: "0x773640d3F836B7819e75F26AE0Ad2bb5f7eBF797",
          lendingRewards:
            "0x300648a601c584E6379fa0D6a31CbBFCcA6177e4",
          lendingPoolExt:
            "0x4550fa2126054c10F5CB9DAE194500bEb20568F8",
          loanTokenExt: "0x1727Ede77c91Da077EBa5B99631a6e982D66F735",
          lendingRewardsExt:
            "0xD44132FAa53126CcaFD0D2d05471d59F88F6B45a",

          mainLp: "0xa6044beb661545339210e300091d4cea4da75236",
          // mainLp: "",
          nativeStablePool:
            "0xc31e54c7a869b9fcbecc14363cf510d1c41fa443",
          twapUtils: "0xdf9a6debb35be847d6addb7843e763539671b2c7",

          bridgeConfigs: "0x77B4d8cB74Ae7C72fbD311A9a13d53baAc049094",
          bridge: "0x95e491daa7abecfb75fcc384c513ab741dd90c16",
        },
      },
      {
        name: "Ethereum Mainnet",
        short_name: "eth",
        chain: "ETH",
        network: "mainnet",
        chain_id: 1,
        network_id: 1,
        chart_url:
          "https://www.dextools.io/app/en/ether/pair-explorer/",
        explorer_url: "https://etherscan.io",
        rpc_url: "https://rpc.mevblocker.io/norefunds",
        dex_url:
          "https://app.uniswap.org/#/swap?inputCurrency=ETH&outputCurrency=%TOKEN%",
        lp_url:
          "https://app.uniswap.org/#/add/v2/ETH/%TOKEN%?chain=mainnet",
        icon: "icon-eth-blue",
        blocks_per_day: 6450,
        native_currency: {
          symbol: "ETH",
          name: "Ether",
          decimals: 18,
          contractAddress:
            "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
          balance: "",
        },
        logo: `/networks/ethereum.svg`,
        contracts: {
          hype: "0x85225Ed797fd4128Ac45A992C46eA4681a7A15dA",

          // TODO: PREVIOUS
          // hlp: "0x387406B6C7d0fac1Fda2a8ab492BB8Ef21DaEAe2",
          // lendingPool: "0xCEB4d728b534281aa903d49Edc7DC8fe90526FB9",
          // loanToken: "0xEb5D3d7D2555F511a079350694D6827469d129a2",
          // lendingRewards:
          //   "0x300648a601c584E6379fa0D6a31CbBFCcA6177e4",
          // lendingPoolExt:
          //   "0x2151bd9a996D032a8157BA8407200cfC6B13a85a",
          // loanTokenExt: "0xD37438d9Bf55216E3A087dD35fB7b5C1ea59B1f0",
          // lendingRewardsExt:
          //   "0xF104778EA449185B454113b6FACEB72d8B583F68",
          hlp: "0x11B13B49f3d327dad6033495A8d9E29aF31201E6",
          lendingPool: "0x6F2064837D82358AFF96E2dFe6AA8953123A0249",
          loanToken: "0xB9ef94C9541fBB6bDe4a1a3d79C44909e268E23e",
          lendingRewards:
            "0x300648a601c584E6379fa0D6a31CbBFCcA6177e4",
          lendingPoolExt:
            "0x8D35a33524C77f542cA2d1AD9c92487E34C90f20",
          loanTokenExt: "0x67Ff4bE79323a9e68454F3D095E33ddbC6D82EB3",
          lendingRewardsExt:
            "0x5D59916adfA0Aa1C02B462483470d84a8089f5f0",

          // mainLp: "0x759dbbd29f06764560b34eae01ca7f48831a9be1",
          mainLp: "0x874f903003dec4639e22cbebff6e8295c80842a7",
          nativeStablePool:
            "0x88e6a0c2ddd26feeb64f039a2c41296fcb3f5640",
          twapUtils: "0xBf1858b24243Ecbcf7d940f458e36CB7401c2366",

          bridgeConfigs: "0xcc0f4d8235b3De2f469A7b9dBc631a0b7E14bA66",
          bridge: "0x08D17392a30a5A3bEb4cB191f5b18005bf494659",
        },
      },
    ],

    selectedCollateralPool: null,
    openMobileSidebar: true,

    // btc: {
    //   currentPrice: 0,
    //   priceChangePercentage: 0,
    // },

    hypeChartData: null,
    ltvHealthThreshold: 75,
  }),

  getters: {
    checkAlreadyConnected(state) {
      return (
        state.builtInWeb3Provider &&
        state.builtInWeb3Provider.selectedAddress
      );
    },

    dataWeb3(_, getters) {
      return new Web3(getters.activeNetwork.rpc_url);
    },

    isConnected(state) {
      return state.web3.isConnected;
    },

    initLoading(state) {
      return state.initLoading;
    },

    activeNetwork(state, getters) {
      return (
        state.networks.find(
          (n) => n.chain_id === state.web3.chainId
        ) || getters.defaultNetwork
      );
    },

    defaultNetwork(state) {
      return state.networks.find(
        (n) => n.short_name === state.selectedNetworkShortName
      );
    },

    userAddress(state) {
      return state.web3.address;
    },

    dexBuyLink(_, getters) {
      return (
        getters.activeNetwork &&
        getters.activeNetwork.dex_url.replace(
          `%TOKEN%`,
          getters.activeNetwork.contracts.hype
        )
      );
    },

    mainDextoolsLink(_, getters) {
      return (
        getters.activeNetwork &&
        `${getters.activeNetwork.chart_url}${getters.activeNetwork.contracts.mainLp}`
      );
    },

    userAddressExplorerLink(_, getters) {
      return (
        getters.activeNetwork &&
        `${getters.activeNetwork.explorer_url}/address/${getters.userAddress}`
      );
    },

    isOnValidNetwork(state) {
      return state.networks.find(
        (n) => n.chain_id == state.web3.chainId
      );
    },

    validNetworkNames(state) {
      return state.networks
        .filter((n) => n.contracts.hype)
        .map((n) => n.name)
        .join(", ");
    },

    nativeCurrencySymbol(_, getters) {
      return (
        getters.activeNetwork &&
        getters.activeNetwork.native_currency.symbol
      );
    },

    nativeCurrencyWrappedAddress(_, getters) {
      return (
        getters.activeNetwork &&
        getters.activeNetwork.native_currency.contractAddress
      );
    },

    tokenSymbol(state) {
      return (state.tokenData && state.tokenData.symbol) || "HYPE";
    },

    userTokenBalanceFixed(state) {
      return (
        (state.userTokenData &&
          new BigNumber(state.userTokenData.balance)
            .div(new BigNumber(10).pow(state.tokenData.decimals))
            .toFixed()) ||
        "0"
      );
    },

    userTokenBalanceFormatted(_, getters) {
      return formatDynamicDecimals(getters.userTokenBalanceFixed);
    },

    userTokenBalanceUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.userTokenBalanceFixed).times(
          getters.tokenPriceUSDFixed
        )
      );
    },

    totalHLPFixed(state) {
      return (
        (state.hlpData &&
          new BigNumber(state.hlpData.totalSupply)
            .div(new BigNumber(10).pow(state.hlpData.decimals))
            .toFixed()) ||
        "0"
      );
    },

    totalHLPFormatted(_, getters) {
      return formatDynamicDecimals(getters.totalHLPFixed);
    },

    totalHLPUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.totalHLPFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    userHLPBalanceFixed(state) {
      return (
        (state.userHLPData &&
          state.hlpData &&
          new BigNumber(state.userHLPData.balance)
            .div(new BigNumber(10).pow(state.hlpData.decimals))
            .toFixed()) ||
        "0"
      );
    },

    userHLPBalanceFormatted(_, getters) {
      return formatDynamicDecimals(getters.userHLPBalanceFixed);
    },

    userHLPBalanceUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.userHLPBalanceFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    currentTaxFixed(state) {
      return (
        (state.tokenData &&
          new BigNumber(state.tokenData.calculateTaxFromAmount)
            .div(new BigNumber(10).pow(state.tokenData.decimals))
            .toFixed()) ||
        0
      );
    },

    currentTaxFormatted(_, getters) {
      return formatDynamicDecimals(getters.currentTaxFixed);
    },

    borrowAprFixed(state) {
      return (
        (state.lendingPoolData &&
          new BigNumber(state.lendingPoolData.calculateBorrowAPR)
            .div(state.denomenator)
            .times(100)
            .toFixed()) ||
        0
      );
    },

    borrowAprFormatted(_, getters) {
      // return formatDynamicDecimals(getters.borrowAprFixed);
      return new BigNumber(getters.borrowAprFixed).toFormat(2);
    },

    lendingPoolMainAvailableFixed(state) {
      return (
        (state.lendingPoolETHBal &&
          new BigNumber(state.lendingPoolETHBal.balance)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    lendingPoolMainAvailableFormatted(_, getters) {
      return new BigNumber(
        getters.lendingPoolMainAvailableFixed
      ).toFormat(2);
    },

    lendingPoolExtAvailableFixed(state) {
      return (
        (state.lendingPoolExtETHBal &&
          new BigNumber(state.lendingPoolExtETHBal.balance)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    lendingPoolExtAvailableFormatted(_, getters) {
      return new BigNumber(
        getters.lendingPoolExtAvailableFixed
      ).toFormat(2);
    },

    lendingPoolAvailableFixed(state) {
      return (
        (state.lendingPoolETHBal &&
          new BigNumber(state.lendingPoolETHBal.balance)
            .plus(state.lendingPoolExtETHBal.balance)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    lendingPoolAvailableFormatted(_, getters) {
      // return formatDynamicDecimals(getters.lendingPoolAvailableFixed);
      return new BigNumber(
        getters.lendingPoolAvailableFixed
      ).toFormat(2);
    },

    lendingPoolUtilizedFormatted(_, getters) {
      return formatDynamicDecimals(
        getters.allFormattedLoans.reduce(
          (bn, loan) =>
            bn.plus(
              new BigNumber(loan.amountETHBorrowed).div(
                new BigNumber(10).pow(18)
              )
            ),
          new BigNumber(0)
        )
      );
    },

    feesDataPerMonth(state, getters) {
      const feeKeyRegexp = /monthlyFees_(\d+)/;
      return (
        (state.rewardsData &&
          Object.keys(state.rewardsData)
            .filter((key) => feeKeyRegexp.test(key))
            .map((key) => {
              const dateUnix = key.replace(feeKeyRegexp, "$1");
              const feesWei = state.rewardsData[key];
              const feesWeiExt = state.rewardsExtData[key];
              const feesETH = new BigNumber(feesWei)
                .plus(feesWeiExt)
                .div(new BigNumber(10).pow(18))
                .toFixed();
              if (new BigNumber(feesWei).eq(0)) {
                return null;
              }
              return {
                monthUnix: dateUnix,
                month: dayjs.unix(dateUnix).utc().format(`MMM YYYY`),
                feesWei,
                feesETH,
                feesUSD: new BigNumber(feesETH)
                  .times(getters.ethPriceUSDFixed)
                  .toFixed(2),
              };
            })
            .filter((f) => f)
            .reverse()) ||
        []
      );
    },

    collateralPools(state) {
      const poolsMain =
        (state.lendingPoolData &&
          state.lendingPoolData.getAllWhitelistedPools) ||
        [];
      const poolsExt =
        (state.lendingPoolExtData &&
          state.lendingPoolExtData.getAllWhitelistedPools) ||
        [];
      const allPools = [
        ...new Set([poolsMain].concat([poolsExt]).flat(Infinity)),
      ];
      return allPools
        .map((poolAddress) => {
          const poolInfo =
            state[`collateralPoolInfo_${poolAddress.toLowerCase()}`];
          if (!poolInfo) {
            return;
          }
          const token0Info =
            state[`tokenInfo_${poolInfo.token0.toLowerCase()}`];
          const userBal0 =
            state[`tokenUserBal_${poolInfo.token0.toLowerCase()}`] &&
            state[`tokenUserBal_${poolInfo.token0.toLowerCase()}`]
              .balanceOf;
          const token1Info =
            state[`tokenInfo_${poolInfo.token1.toLowerCase()}`];
          const userBal1 =
            state[`tokenUserBal_${poolInfo.token1.toLowerCase()}`] &&
            state[`tokenUserBal_${poolInfo.token1.toLowerCase()}`]
              .balanceOf;
          return {
            ...poolInfo,
            address: poolAddress,
            token0: {
              ...token0Info,
              address: poolInfo.token0,
              userBalance: userBal0 || "0",
            },
            token1: {
              ...token1Info,
              address: poolInfo.token1,
              userBalance: userBal1 || "0",
            },
          };
        })
        .filter((p) => p);
    },

    loanCollateralTokens(_, getters) {
      return getters.collateralPools
        .map((info) => {
          const is0WETH =
            info.token0.address.toLowerCase() ==
            getters.nativeCurrencyWrappedAddress.toLowerCase();
          const altToken = is0WETH ? info.token1 : info.token0;
          if (!altToken.decimals) {
            return null;
          }
          const sqrtPriceRawX96 = info.slot0[0];
          const priceRawX96 = new BigNumber(sqrtPriceRawX96)
            .pow(2)
            .div(new BigNumber(2).pow(96))
            .toFixed();
          const priceETHNumX96 = is0WETH
            ? new BigNumber(new BigNumber(2).pow(96 * 2))
                .div(priceRawX96)
                .toFixed()
            : priceRawX96;
          const priceETHNoDecimals = new BigNumber(priceETHNumX96)
            .times(new BigNumber(10).pow(altToken.decimals))
            .div(new BigNumber(10).pow(18))
            .div(new BigNumber(2).pow(96));
          return {
            pool: info.address,
            token: {
              ...altToken,
              img: altToken.symbol
                ? `/img/${altToken.symbol.toLowerCase()}.png`
                : `/img/blank.png`,
            },
            priceUSD: priceETHNoDecimals
              .times(getters.ethPriceUSDFixed)
              .toFixed(),
          };
        })
        .filter((t) => t)
        .sort((t1, t2) =>
          t1.token.symbol.toLowerCase() <
          t2.token.symbol.toLowerCase()
            ? -1
            : 1
        );
    },

    allTokensInCollateralPools(state) {
      return (
        (state.lendingPoolData &&
          state.lendingPoolData.getAllWhitelistedPools.reduce(
            (ary, pool) => {
              const poolInfo =
                state[`collateralPoolInfo_${pool.toLowerCase()}`];
              ary.push(poolInfo.token0, poolInfo.token1);
              return [...new Set(ary)];
            },
            []
          )) ||
        []
      );
    },

    allFormattedLoans(state, getters) {
      const loanKeyRegexp = /loan_(\d+)/;
      const loanKeys =
        (state.loanSecondaryInfo &&
          Object.keys(state.loanSecondaryInfo)) ||
        [];
      const loanKeysExt =
        (state.loanSecondaryExtInfo &&
          Object.keys(state.loanSecondaryExtInfo)) ||
        [];
      const loanKeysAgg = loanKeys
        .map((key) => ({
          key,
          contract: getters.activeNetwork.contracts.lendingPool,
        }))
        .concat(
          loanKeysExt.map((key) => ({
            key,
            contract: getters.activeNetwork.contracts.lendingPoolExt,
          }))
        );
      return loanKeysAgg
        .map(({ key, contract: lendingPoolContract }) => {
          if (!loanKeyRegexp.test(key)) {
            return;
          }
          const isMainPool =
            lendingPoolContract.toLowerCase() ===
            getters.activeNetwork.contracts.lendingPool.toLowerCase();
          const tokenId = key.replace(loanKeyRegexp, "$1");
          const loan = isMainPool
            ? state.loanSecondaryInfo[key]
            : state.loanSecondaryExtInfo[key] || "";
          const ltvInfo = isMainPool
            ? state.loanSecondaryInfo[`ltvX96_${tokenId}`]
            : state.loanSecondaryExtInfo[`ltvX96_${tokenId}`] || [];
          const owner = isMainPool
            ? state.loanOwnerInfo[`ownerOf_${tokenId}`]
            : state.loanOwnerExtInfo[`ownerOf_${tokenId}`] || "";
          const [
            created,
            createdBy,
            aprStart,
            collateralPool,
            amountDeposited,
            amountETHBorrowed,
          ] = loan;
          const [
            ltvX96,
            amountETHDepositedX96,
            amountETHBorrowedX96,
          ] = ltvInfo;
          let collateralPoolInfo = getters.collateralPools.find(
            (p) =>
              p.address.toLowerCase() === collateralPool.toLowerCase()
          );
          const defaultToken = {
            address: state.zeroAddress,
            decimals: 18,
          };
          if (!collateralPoolInfo) {
            collateralPoolInfo =
              state[
                `secCollateralPoolInfo_${collateralPool.toLowerCase()}`
              ];
            if (collateralPoolInfo) {
              collateralPoolInfo = {
                ...collateralPoolInfo,
                token0: {
                  address: collateralPoolInfo.token0,
                  ...state[
                    `tokenInfo_${collateralPoolInfo.token0.toLowerCase()}`
                  ],
                },
                token1: {
                  address: collateralPoolInfo.token1,
                  ...state[
                    `tokenInfo_${collateralPoolInfo.token1.toLowerCase()}`
                  ],
                },
              };
            } else {
              collateralPoolInfo = {
                token0: defaultToken,
                token1: defaultToken,
                slot0: [0],
              };
            }
          }

          const is0WETH =
            collateralPoolInfo.token0.address.toLowerCase() ==
            getters.nativeCurrencyWrappedAddress.toLowerCase();
          const altToken = is0WETH
            ? collateralPoolInfo.token1
            : collateralPoolInfo.token0;
          const sqrtPriceRawX96 = collateralPoolInfo.slot0[0];
          const priceRawX96 = new BigNumber(sqrtPriceRawX96)
            .pow(2)
            .div(new BigNumber(2).pow(96))
            .toFixed();
          const priceETHNumX96 = is0WETH
            ? new BigNumber(new BigNumber(2).pow(96 * 2))
                .div(priceRawX96)
                .toFixed()
            : priceRawX96;
          const priceETHNoDecimals = new BigNumber(priceETHNumX96)
            .times(new BigNumber(10).pow(altToken.decimals))
            .div(new BigNumber(10).pow(18))
            .div(new BigNumber(2).pow(96));

          return {
            lendingPoolContract,
            owner,
            tokenId,
            created,
            createdBy,
            aprStart,
            collateralPool,
            collateralPoolInfo,
            collateralPriceUSD: priceETHNoDecimals
              .times(getters.ethPriceUSDFixed)
              .toFixed(),
            amountDeposited,
            amountETHBorrowed,
            ltvX96,
            ltv: new BigNumber(ltvX96)
              .div(new BigNumber(2).pow(96))
              .toFixed(),
            amountETHDepositedX96,
            amountETHDeposited: new BigNumber(amountETHDepositedX96)
              .div(new BigNumber(2).pow(96))
              .toFixed(),
            amountETHBorrowedX96,
          };
        })
        .sort((l1, l2) =>
          new BigNumber(l1.tokenId).minus(l2.tokenId).toNumber()
        )
        .filter((l) => l);
    },

    userOwnedLoans(state, getters) {
      const userAddy = state.web3.address;
      if (!userAddy) return [];
      return getters.allFormattedLoans.filter(
        (l) =>
          typeof l.owner === "string" &&
          l.owner.toLowerCase() === userAddy.toLowerCase()
      );
    },

    ethRewardsTotalOverallFixed(state) {
      return (
        (state.rewardsData &&
          new BigNumber(state.rewardsData.totalRewards)
            .plus(state.rewardsExtData.totalRewards)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    ethRewardsTotalOverallFormatted(_, getters) {
      return formatDynamicDecimals(
        getters.ethRewardsTotalOverallFixed
      );
    },

    ethRewardsTotalOverallUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.ethRewardsTotalOverallFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    ethRewardsTotalRewardsFixed(state) {
      return (
        (state.rewardsData &&
          new BigNumber(state.rewardsData.totalRewards)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    ethRewardsTotalRewardsFormatted(_, getters) {
      return formatDynamicDecimals(
        getters.ethRewardsTotalRewardsFixed
      );
    },

    ethRewardsTotalRewardsUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.ethRewardsTotalRewardsFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    ethRewardsUserUnclaimedFixed(state) {
      return (
        (state.userRewardsData &&
          new BigNumber(state.userRewardsData.userUnpaid)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    ethRewardsUserHasUnclaimed(_, getters) {
      return new BigNumber(getters.ethRewardsUserUnclaimedFixed).gt(
        0
      );
    },

    ethRewardsUserUnclaimedFormatted(_, getters) {
      return formatDynamicDecimals(
        getters.ethRewardsUserUnclaimedFixed
      );
    },

    ethRewardsUserUnclaimedUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.ethRewardsUserUnclaimedFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    ethRewardsUserTotalFixed(state) {
      if (!(state.userRewardsData && state.userRewardsData.rewards)) {
        return 0;
      }
      const realized = state.userRewardsData.rewards[1];
      return (
        new BigNumber(state.userRewardsData.userUnpaid)
          .plus(realized)
          .div(new BigNumber(10).pow(18))
          .toFixed() || 0
      );
    },

    ethRewardsUserTotalFormatted(state, getters) {
      return formatDynamicDecimals(getters.ethRewardsUserTotalFixed);
    },

    ethRewardsUserTotalUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.ethRewardsUserTotalFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    ethRewardsExtTotalRewardsFixed(state) {
      return (
        (state.rewardsExtData &&
          new BigNumber(state.rewardsExtData.totalRewards)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    ethRewardsExtTotalRewardsFormatted(_, getters) {
      return formatDynamicDecimals(
        getters.ethRewardsExtTotalRewardsFixed
      );
    },

    ethRewardsExtTotalRewardsUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.ethRewardsExtTotalRewardsFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    ethRewardsExtUserUnclaimedFixed(state) {
      return (
        (state.userRewardsExtData &&
          new BigNumber(state.userRewardsExtData.userUnpaid)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    ethRewardsExtUserHasUnclaimed(_, getters) {
      return new BigNumber(
        getters.ethRewardsExtUserUnclaimedFixed
      ).gt(0);
    },

    ethRewardsExtUserUnclaimedFormatted(_, getters) {
      return formatDynamicDecimals(
        getters.ethRewardsExtUserUnclaimedFixed
      );
    },

    ethRewardsExtUserUnclaimedUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.ethRewardsExtUserUnclaimedFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    ethRewardsExtUserTotalFixed(state) {
      if (
        !(
          state.userRewardsExtData && state.userRewardsExtData.rewards
        )
      ) {
        return 0;
      }
      const realized = state.userRewardsExtData.rewards[1];
      return (
        new BigNumber(state.userRewardsExtData.userUnpaid)
          .plus(realized)
          .div(new BigNumber(10).pow(18))
          .toFixed() || 0
      );
    },

    ethRewardsExtUserTotalFormatted(_, getters) {
      return formatDynamicDecimals(
        getters.ethRewardsExtUserTotalFixed
      );
    },

    ethRewardsExtUserTotalUSDFormatted(_, getters) {
      return formatDynamicDecimals(
        new BigNumber(getters.ethRewardsExtUserTotalFixed).times(
          getters.ethPriceUSDFixed
        )
      );
    },

    ethPriceUSDFixed(state, getters) {
      if (!state.nativeStableData) return "0.00";
      const wrappedNative =
        getters.activeNetwork.native_currency.contractAddress.toLowerCase();
      const token0 =
        typeof state.nativeStableData.token0 === "string" &&
        state.nativeStableData.token0.toLowerCase();
      // const token1 = state.nativeStableData.token1.toLowerCase();
      if (!token0) {
        return "0.00";
      }
      const sqrtPriceX96 = state.nativeStableData.slot0[0];
      const priceX96 = new BigNumber(sqrtPriceX96)
        .pow(2)
        .div(new BigNumber(2).pow(96));
      const usdPriceX96WithDecimals =
        token0 === wrappedNative
          ? priceX96
          : new BigNumber(2).pow(96).pow(2).div(priceX96);
      const usdPriceX96 = usdPriceX96WithDecimals
        .times(new BigNumber(10).pow(18))
        .div(new BigNumber(10).pow(6));
      return usdPriceX96.div(new BigNumber(2).pow(96)).toFixed();
    },

    tokenPriceUSDFixed(state, getters) {
      if (!state.tokenLPData) return "0.00";
      const hype = getters.activeNetwork.contracts.hype.toLowerCase();
      const token0 = state.tokenLPData.token0.toLowerCase();
      // const token1 = state.nativeStableData.token1.toLowerCase();
      const sqrtPriceX96 = state.tokenLPData.slot0[0];
      const priceX96 = new BigNumber(sqrtPriceX96)
        .pow(2)
        .div(new BigNumber(2).pow(96));
      const wethPriceX96WithDecimals =
        token0 === hype
          ? priceX96
          : new BigNumber(2).pow(96 * 2).div(priceX96);
      const wethPriceX96 =
        token0 === hype
          ? wethPriceX96WithDecimals
              .times(new BigNumber(10).pow(18))
              .div(new BigNumber(10).pow(state.tokenData.decimals))
          : wethPriceX96WithDecimals
              .times(new BigNumber(10).pow(state.tokenData.decimals))
              .div(new BigNumber(10).pow(18));
      return wethPriceX96
        .times(getters.ethPriceUSDFixed)
        .div(new BigNumber(2).pow(96))
        .toFixed();
    },

    tokenPriceUSDFormatted(_, getters) {
      return formatDynamicDecimals(getters.tokenPriceUSDFixed);
    },

    userETHBalanceFixed(state) {
      return (
        (state.userETHBal &&
          new BigNumber(state.userETHBal.balance)
            .div(new BigNumber(10).pow(18))
            .toFixed()) ||
        0
      );
    },

    userETHBalanceFormatted(_, getters) {
      return formatDynamicDecimals(getters.userETHBalanceFixed);
    },

    openMobileSidebar(state) {
      return state.openMobileSidebar;
    },

    hypeChartData(state) {
      return state.hypeChartData;
    },

    userAddressDisplay(state) {
      return state.web3.ens
        ? state.web3.ens
        : getShortAddress(state.web3.address);
    },

    userAddressAvailable(state, getters) {
      return getters.userAddressDisplay !== "...";
    },
  },

  mutations: {
    SET_INIT_LOADING(state, isLoading) {
      state.initLoading = isLoading;
    },

    SET_INIT_DATA_OBJ(state, { key, obj }) {
      state[key] = obj;
    },

    SET_SELECTED_ADDRESS(state, address) {
      state.selectedAddressInfo.address = address;
    },

    SET_SELECTED_ADDRESS_INFO(state, info) {
      const keys = Object.keys(info);
      state.selectedAddressInfo = {
        ...state.selectedAddressInfo,
        ...keys.reduce((o, key) => ({ ...o, [key]: info[key] }), {}),
      };
    },

    SET_SELECTED_COLLATERAL_POOL(state, info) {
      state.selectedCollateralPool = info;
    },

    SET_WEB3_KEY(state, { key, val }) {
      state.web3 = {
        ...state.web3,
        [key]: val,
      };
    },

    SET_OPEN_MOBILE_SIDEBAR(state, status) {
      state.openMobileSidebar = status;
    },

    SET_SELECTED_NETWORK_SHORT_NAME(state, shortName) {
      state.selectedNetworkShortName = shortName;
    },

    SET_HYPE_PRICE_INFO(state, [data1, data2]) {
      // state.btc.currentPrice = data1.market_data.current_price.usd;
      // state.btc.priceChangePercentage =
      //   data1.market_data.price_change_percentage_24h;
      state.hypeChartData = data2.prices;
    },

    SET_ENS(state, { address, ens }) {
      state.addressEnsMap = {
        ...state.addressEnsMap,
        [address]: ens,
      };
    },
  },

  actions: {
    async initWithoutBeingConnected(
      { commit, getters, state },
      reset = false
    ) {
      try {
        commit("SET_INIT_LOADING", true);
        if (state.noConnectDataRefresh && !reset) {
          return;
        }
        const web3 = getters.dataWeb3;
        const results = await executeMulticall(
          web3,
          OnChainDataInfo.initCalls,
          getters.activeNetwork.contracts
        );
        processMulticallCallsReturnContext(
          results,
          OnChainDataInfo.initCalls,
          (data) => {
            commit("SET_INIT_DATA_OBJ", data);
            // console.log("INITINFOOOOOOO", data.key, data.obj);
          }
        );

        const results2 = await executeMulticall(
          web3,
          OnChainDataInfo.initCalls2,
          getters.activeNetwork.contracts
        );
        processMulticallCallsReturnContext(
          results2,
          OnChainDataInfo.initCalls2,
          (data) => {
            commit("SET_INIT_DATA_OBJ", data);
            // console.log("INITINFOOOOOOO2222", data.key, data.obj);
          }
        );

        const openedLoansMain = !state.loanTokenData
          ? []
          : state.loanTokenData.getAllOpenedLoans instanceof Array
          ? state.loanTokenData.getAllOpenedLoans
          : [state.loanTokenData.getAllOpenedLoans];
        const openedLoansExt = !state.loanTokenData
          ? []
          : state.loanTokenExtData.getAllOpenedLoans instanceof Array
          ? state.loanTokenExtData.getAllOpenedLoans
          : [state.loanTokenExtData.getAllOpenedLoans];
        const loanCalls = OnChainDataInfo.loanCalls(
          (state.lendingPoolData &&
            state.lendingPoolData.getAllWhitelistedPools) ||
            [],
          openedLoansMain,
          openedLoansExt,
          getters.activeNetwork.contracts.lendingPool,
          getters.activeNetwork.contracts.lendingPoolExt
        );
        const loanResults = await executeMulticall(
          web3,
          loanCalls,
          getters.activeNetwork.contracts
        );
        processMulticallCallsReturnContext(
          loanResults,
          loanCalls,
          (data) => {
            commit("SET_INIT_DATA_OBJ", data);
            // console.log("LOANNNNINFOOOOOO", data.key, data.obj);

            if (
              ["loanOwnerInfo", "loanOwnerExtInfo"].includes(
                data.key
              ) &&
              data.obj
            ) {
              const ensUtils = EnsUtils(state.ensRpcUrl);
              Object.keys(data.obj).forEach(async (key) => {
                const address =
                  typeof data.obj[key] === "string"
                    ? data.obj[key].toLowerCase()
                    : null;
                try {
                  if (!address || state.addressEnsMap[address]) {
                    return;
                  }
                  const ens = await ensUtils.getMainENS(address);
                  commit("SET_ENS", { address, ens });
                } catch (err) {
                  console.info(`Error getting ENS`, err);
                  commit("SET_ENS", { address, ens: null });
                }
              });
            }
          }
        );

        let tokensInAllLoans = [];
        const loanPoolCalls = OnChainDataInfo.poolCalls([
          ...new Set(
            getters.allFormattedLoans
              .map((l) => l.collateralPool)
              .filter((p) => new BigNumber(p.toLowerCase()).gt(0))
          ),
        ]);
        // console.log("LOANPOOLS", getters.allFormattedLoans);
        const loanPoolResults = await executeMulticall(
          web3,
          loanPoolCalls,
          getters.activeNetwork.contracts
        );
        processMulticallCallsReturnContext(
          loanPoolResults,
          loanPoolCalls,
          (data) => {
            commit("SET_INIT_DATA_OBJ", data);
            tokensInAllLoans.push(data.obj.token0, data.obj.token1);
            // console.log("LOANNNPOOOLINFOOO", data.key, data.obj);
          }
        );

        const tokenCalls = OnChainDataInfo.tokenDetailCalls([
          ...new Set([
            ...getters.allTokensInCollateralPools,
            ...tokensInAllLoans,
          ]),
        ]);
        const tokenResults = await executeMulticall(
          web3,
          tokenCalls,
          getters.activeNetwork.contracts
        );
        processMulticallCallsReturnContext(
          tokenResults,
          tokenCalls,
          (data) => {
            commit("SET_INIT_DATA_OBJ", data);
            // console.log("TOKENNNNINFOOOOOO", data.key, data.obj);
          }
        );
      } catch (err) {
        console.error(err);
      } finally {
        commit("SET_INIT_LOADING", false);
      }
    },

    async initUserData({ commit, getters, state }) {
      const address = state.web3.address;
      const web3 = getters.dataWeb3;
      const userCalls = OnChainDataInfo.userCalls(
        address,
        getters.allTokensInCollateralPools
      );
      const results = await executeMulticall(
        web3,
        userCalls,
        getters.activeNetwork.contracts
      );
      processMulticallCallsReturnContext(
        results,
        userCalls,
        (data) => {
          commit("SET_INIT_DATA_OBJ", data);
          // console.log("USERRRINFOOOOOOO", data.key, data.obj);
        }
      );
    },

    async init({ commit, dispatch, getters, state }, reset = false) {
      try {
        if (
          state.web3 &&
          state.web3.isConnected &&
          getters.userAddressAvailable &&
          !reset
        )
          return;
        commit("SET_INIT_LOADING", true);
        let provider, web3;
        if (getters.checkAlreadyConnected) {
          provider = Web3Modal.provider = window.ethereum;
          web3 = Web3Modal.web3 = new Web3(provider);
          await web3.eth.requestAccounts();
        } else {
          const { provider: p, web3: w3 } = await Web3Modal.connect();
          provider = p;
          web3 = w3;
        }
        commit("SET_WEB3_KEY", { key: "provider", val: provider });
        commit("SET_WEB3_KEY", { key: "instance", val: web3 });
        commit("SET_WEB3_KEY", { key: "isConnected", val: true });
        const resetConnection = async () => {
          await dispatch("disconnect");
          await dispatch("init", true);
          localStorage.WEB3_CONNECT_CACHED_PROVIDER = '"injected"';
        };
        Web3Modal.bindProviderEvents({
          accountsChanged: resetConnection,
          chainChanged: resetConnection,
          disconnect: () => dispatch("disconnect"),
        });

        commit("SET_WEB3_KEY", {
          key: "chainId",
          val: await web3.eth.getChainId(),
        });
        const [accountAddy] = await web3.eth.getAccounts();
        commit("SET_WEB3_KEY", { key: "address", val: accountAddy });
        try {
          const ens = await EnsUtils(state.ensRpcUrl).getMainENS(
            accountAddy
          );
          commit("SET_ENS", {
            address: accountAddy.toLowerCase(),
            ens,
          });
        } catch (err) {
          console.error(`ENS`, err);
        }

        await dispatch("initWithoutBeingConnected");
        await dispatch("initUserData");

        // if (!state.dataRefreshInterval) {
        //   commit(
        //     "SET_DATA_REFRESH_INTERVAL",
        //     setInterval(() => {
        //       dispatch("initWithoutBeingConnected");
        //       dispatch("init");
        //     }, 15000)
        //   );
        // }
      } catch (err) {
        console.error(err);
      } finally {
        commit("SET_INIT_LOADING", false);
      }
    },

    async disconnect({ commit }) {
      commit("SET_WEB3_KEY", { key: "provider", val: null });
      commit("SET_WEB3_KEY", { key: "instance", val: null });
      commit("SET_WEB3_KEY", { key: "isConnected", val: false });
      commit("SET_WEB3_KEY", { key: "chainId", val: null });
      commit("SET_WEB3_KEY", { key: "address", val: "" });

      // Clear cached provider to be able to switch between providers when disconnecting wallet
      await Web3Modal.clearCachedProvider();
    },

    async setSelectedNetwork({ commit, dispatch }, networkShortName) {
      commit("SET_SELECTED_NETWORK_SHORT_NAME", networkShortName);
      await dispatch("initWithoutBeingConnected", true);
    },

    async depositAndBorrow(
      { dispatch, getters, state },
      {
        lendingContract,
        tokenId,
        collateralToken,
        pool,
        depositAmount,
        borrowAmount,
      }
    ) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;
      const lendingPoolMainETH = state.lendingPoolETHBal.balance;
      const lendingPoolExtETH = state.lendingPoolExtETHBal.balance;
      let lendingPool = lendingContract;
      if (new BigNumber(tokenId).eq(0)) {
        lendingPool = getters.activeNetwork.contracts.lendingPool;
        if (new BigNumber(lendingPoolMainETH).lt(borrowAmount)) {
          const currentBorrowForPool =
            state.collPoolExtMaxInfo[
              `currentBorrowPerPool_${pool.toLowerCase()}`
            ];
          const maxBorrowForPool =
            state.collPoolExtMaxInfo[
              `maxBorrowPerPool_${pool.toLowerCase()}`
            ];

          // borrow amount for this specific collateral token pool exceeds max to fulfill loan
          if (
            new BigNumber(maxBorrowForPool).gt(0) &&
            new BigNumber(currentBorrowForPool)
              .plus(borrowAmount)
              .gt(maxBorrowForPool)
          ) {
            const availableToBorrowFormatted = formatDynamicDecimals(
              new BigNumber(maxBorrowForPool)
                .minus(currentBorrowForPool)
                .div(new BigNumber(10).pow(18))
            );
            throw new Error(
              `There is only ${availableToBorrowFormatted} ${getters.nativeCurrencySymbol} avaialable left for borrow for this collateral token.`
            );
          }

          // lending pool does not have enough ETH to fulfill loan
          if (new BigNumber(lendingPoolExtETH).lt(borrowAmount)) {
            throw new Error(
              `Not enough liquidity to fulfill this loan.`
            );
          }
          lendingPool =
            getters.activeNetwork.contracts.lendingPoolExt;
        }
      } else if (new BigNumber(borrowAmount || 0).gt(0)) {
        let lendingPoolETH = lendingPoolMainETH;
        if (
          lendingPool.toLowerCase() ===
          getters.activeNetwork.contracts.lendingPoolExt.toLowerCase()
        ) {
          lendingPoolETH = lendingPoolExtETH;
        }
        if (new BigNumber(lendingPoolETH).lt(borrowAmount)) {
          throw new Error(
            `Not enough liquidity. You can borrow up to ${new BigNumber(
              lendingPoolETH
            )
              .div(new BigNumber(10).pow(18))
              .toFormat(4)} ${getters.nativeCurrencySymbol}.`
          );
        }
      }

      const isDepositing = new BigNumber(depositAmount).gt(0);
      const isBorrowing = new BigNumber(borrowAmount).gt(0);

      if (isDepositing) {
        await dispatch("genericErc20Approval", {
          spendAmount: depositAmount,
          tokenAddress: collateralToken,
          delegateAddress: lendingPool,
        });
      }

      if (isDepositing && isBorrowing) {
        await LendingPool(web3, lendingPool)
          .methods.depositAndBorrow(
            tokenId,
            pool,
            depositAmount,
            borrowAmount
          )
          .send({
            from: userAddy,
            maxPriorityFeePerGas: null,
            maxFeePerGas: null,
          });
      } else if (isBorrowing) {
        await LendingPool(web3, lendingPool)
          .methods.borrow(tokenId, borrowAmount)
          .send({
            from: userAddy,
            maxPriorityFeePerGas: null,
            maxFeePerGas: null,
          });
      } else if (isDepositing) {
        await LendingPool(web3, lendingPool)
          .methods.deposit(tokenId, pool, depositAmount)
          .send({
            from: userAddy,
            maxPriorityFeePerGas: null,
            maxFeePerGas: null,
          });
      }
      await dispatch("initWithoutBeingConnected");
      await dispatch("initUserData");
    },

    async withdraw(
      { dispatch, state },
      { lendingContract, tokenId, amount }
    ) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;
      await LendingPool(web3, lendingContract)
        .methods.withdraw(tokenId, amount)
        .send({
          from: userAddy,
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        });
      await dispatch("initWithoutBeingConnected");
      await dispatch("initUserData");
    },

    async payBackLoan(
      { dispatch, state },
      { lendingContract, tokenId, feesAccumulatedWei, value }
    ) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;

      const loan = await LendingPool(web3, lendingContract)
        .methods.loans(tokenId)
        .call();
      const payingBackInFull = new BigNumber(value).gte(
        new BigNumber(loan.amountETHBorrowed).times("0.995")
      );
      let payBackMethod = "payBackLoan";
      let multiplier = "1";
      if (payingBackInFull) {
        payBackMethod = "payBackLoanAndClose";
        value = new BigNumber(value)
          .plus(feesAccumulatedWei)
          .toFixed(0);
        multiplier = "1.005";
      }
      await LendingPool(web3, lendingContract)
        .methods[payBackMethod](tokenId)
        .send({
          from: userAddy,
          value: new BigNumber(value).times(multiplier).toFixed(0),
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        });
      await dispatch("initWithoutBeingConnected");
      await dispatch("initUserData");
    },

    async ethRewardsClaim({ dispatch, getters }) {
      await dispatch(
        "ethRewardsClaimBase",
        getters.activeNetwork.contracts.lendingRewards
      );
    },

    async ethRewardsExtClaim({ dispatch, getters }) {
      await dispatch(
        "ethRewardsClaimBase",
        getters.activeNetwork.contracts.lendingRewardsExt
      );
    },

    async ethRewardsClaimBase({ dispatch, state }, rewardsContract) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;
      await LendingRewards(web3, rewardsContract)
        .methods.claimReward()
        .send({
          from: userAddy,
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        });
      await dispatch("initWithoutBeingConnected");
      await dispatch("initUserData");
    },

    async buyHLP({ dispatch, getters, state }, amount) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;
      const extPool = getters.activeNetwork.contracts.lendingPoolExt;
      await LendingPoolExtLP(web3, extPool)
        .methods.depositLiquidity()
        .send({
          from: userAddy,
          value: amount,
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        });
      await dispatch("initWithoutBeingConnected");
      await dispatch("initUserData");
    },

    async sellHLP({ dispatch, getters, state }, amount) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;
      const extPool = getters.activeNetwork.contracts.lendingPoolExt;
      const hlp = getters.activeNetwork.contracts.hlp;

      const extETHBal = state.lendingPoolExtETHBal.balance;
      if (new BigNumber(amount).gt(extETHBal)) {
        throw new Error(
          `Not enough unutilized liquidity to withdraw ${formatDynamicDecimals(
            new BigNumber(amount).div(new BigNumber(10).pow(18))
          )} ${
            getters.nativeCurrencySymbol
          }. Please wait until HLP utilization goes down or withdraw a smaller amount.`
        );
      }

      const hlpBal = await ERC20(web3, hlp)
        .methods.balanceOf(userAddy)
        .call();
      if (new BigNumber(amount).gt(hlpBal)) {
        throw new Error(
          `You may only withdraw up to your HLP balance of ${formatDynamicDecimals(
            new BigNumber(hlpBal).div(new BigNumber(10).pow(18))
          )} HLP.`
        );
      }

      await LendingPoolExtLP(web3, extPool)
        .methods.withdrawLiquidity(amount)
        .send({
          from: userAddy,
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        });
      await dispatch("initWithoutBeingConnected");
      await dispatch("initUserData");
    },

    async bridgeTokens({ getters, state, dispatch }, amountTokens) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;
      const bridge = getters.activeNetwork.contracts.bridge;
      const hype = getters.activeNetwork.contracts.hype;
      const bridgeContract = CCIPBridge(web3, bridge);
      const amountRaw = new BigNumber(amountTokens)
        .times(new BigNumber(10).pow(state.tokenData.decimals))
        .toFixed(0);
      const targetNetwork = state.networks.find((n) =>
        getters.activeNetwork.network == "arbitrum"
          ? n.network == "mainnet"
          : n.network == "arbitrum"
      );
      const isMintBurn =
        getters.activeNetwork.network == "mainnet" ? false : true;
      const targetChain =
        getters.activeNetwork.network == "arbitrum"
          ? "5009297550715157269"
          : "4949039107694359620";
      const targetBridge = targetNetwork.contracts.bridge;

      await dispatch("genericErc20Approval", {
        spendAmount: amountRaw,
        tokenAddress: hype,
        delegateAddress: bridge,
      });
      const ccipFee = await bridgeContract.methods
        .getMsgFee(
          [true, targetBridge, hype, isMintBurn, targetChain, hype],
          userAddy,
          amountRaw
        )
        .call();

      await bridgeContract.methods
        .bridge(targetChain, userAddy, hype, amountRaw)
        .send({
          from: userAddy,
          value: ccipFee,
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        });

      await dispatch("initWithoutBeingConnected");
      await dispatch("initUserData");
    },

    async getErc20TokenInfo({ state }, tokenAddy) {
      const userAddy = state.web3.address;
      const web3 = state.web3.instance;
      const multicall = new Multicall({
        web3Instance: web3,
        tryAggregate: true,
      });
      const erc20Context = [
        {
          reference: "erc20Info",
          contractAddress: tokenAddy,
          abi: erc20Abi,
          calls: [
            {
              reference: "ERC20",
              methodName: "name",
              methodParameters: [],
            },
            {
              reference: "ERC20",
              methodName: "symbol",
              methodParameters: [],
            },
            {
              reference: "ERC20",
              methodName: "decimals",
              methodParameters: [],
            },
            {
              reference: "ERC20",
              methodName: "balanceOf",
              methodParameters: [userAddy],
            },
          ],
        },
      ];
      const { results: erc20MC } = await multicall.call(erc20Context);
      const erc20Info =
        erc20MC.erc20Info && erc20MC.erc20Info.callsReturnContext;
      const { name, symbol, decimals, userBalance } = {
        name: processMulticallValue(erc20Info[0].returnValues[0]),
        symbol: processMulticallValue(erc20Info[1].returnValues[0]),
        decimals: processMulticallValue(erc20Info[2].returnValues[0]),
        userBalance: processMulticallValue(
          erc20Info[3].returnValues[0]
        ),
      };
      return {
        address: tokenAddy,
        name,
        symbol,
        decimals,
        userBalance,
      };
    },

    async genericErc20Approval(
      { state },
      { spendAmount, tokenAddress, delegateAddress, unlimited }
    ) {
      if (new BigNumber(spendAmount || 0).lte(0)) return;

      unlimited = unlimited === false ? false : true;
      const userAddy = state.web3.address;
      const contract = ERC20(state.web3.instance, tokenAddress);
      const [userBalance, currentAllowance] = await Promise.all([
        contract.methods.balanceOf(userAddy).call(),
        contract.methods.allowance(userAddy, delegateAddress).call(),
      ]);
      if (new BigNumber(currentAllowance).lte(spendAmount || 0)) {
        await contract.methods
          .approve(
            delegateAddress,
            unlimited
              ? new BigNumber(2).pow(256).minus(1).toFixed()
              : userBalance
          )
          .send({
            from: userAddy,
            maxPriorityFeePerGas: null,
            maxFeePerGas: null,
          });
      }
    },

    async getBtcData({ commit }) {
      const [{ data: data1 }, { data: data2 }] =
        await exponentialBackoff(
          async () =>
            await axios.all([
              axios.get(
                "https://api.coingecko.com/api/v3/coins/hyperbolic-protocol"
              ),
              axios.get(
                "https://api.coingecko.com/api/v3/coins/hyperbolic-protocol/market_chart?vs_currency=usd&days=7"
              ),
            ])
        );
      commit("SET_HYPE_PRICE_INFO", [data1, data2]);
    },
  },
};
